import React, { useRef, useState, KeyboardEvent, ChangeEvent } from 'react';
import classnames from 'classnames';

import SearchInput from 'components/SearchInput/SearchInput';
import SearchNavigateBack from 'components/SearchNavigateBack/SearchNavigateBack';
import SearchResult from 'components/SearchResult/SearchResult';
import SearchError from 'components/SearchError/SearchError';
import Spinner from '@johnlewispartnership/wtr-ingredients/ingredients/Spinner';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography';

import { keyValues } from 'constants/keys';

import searchResultStyles from 'components/SearchResult/SearchResult.scss';
import styles from './AddressSearch.scss';

interface Item {
  id: string;
  name: string;
  extraData: string;
}

interface AddressSearchProps {
  canNavigateBack: boolean;
  dataTag?: string;
  error?: boolean;
  loading: boolean;
  heldValue: string;
  id: string;
  onChange: (searchTerm: string) => void;
  onNavigateBack: () => void;
  onSelect: (item: Item) => void;
  searchItems: Item[];
  required?: boolean;
  validationError?: string;
}

interface AddressSearchState {
  addressSelected: boolean;
  value: string;
  hasFocus: boolean;
}

const AddressSearch: React.FC<AddressSearchProps> = ({
  canNavigateBack,
  dataTag = 'search',
  error = false,
  loading,
  heldValue,
  id,
  onChange,
  onNavigateBack,
  onSelect,
  searchItems,
  required = false,
  validationError = '',
}) => {
  const [state, setState] = useState<AddressSearchState>({
    addressSelected: false,
    value: heldValue,
    hasFocus: false,
  });

  const { value, hasFocus, addressSelected } = state;

  const searchItemPrefix = `search-item_`;
  const totalItems = canNavigateBack ? searchItems.length + 1 : searchItems.length;
  const searchContainer = useRef<HTMLDivElement>(null);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const searchTerm = event.target.value || '';
    const isSameSearchTerm = value === searchTerm;
    setState({ ...state, value: searchTerm, addressSelected: false });
    if (searchTerm.trim().length > 1 && !isSameSearchTerm) {
      onChange(searchTerm);
    }
  };

  const handleSearchFocus = (): void => {
    setState({ ...state, hasFocus: true });
  };

  const handleSearchBlur = (): void => {
    setTimeout(() => {
      if (searchContainer.current && !searchContainer.current.contains(document.activeElement)) {
        setState({ ...state, hasFocus: false });
      }
    }, 20);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    const items = document.querySelectorAll(`#search-navigate-back, [id^=${searchItemPrefix}]`);
    const firstItem = items[0] as HTMLElement;
    const lastItem = items[items.length - 1] as HTMLElement;

    if (firstItem && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      event.stopPropagation();

      if (event.key === 'ArrowDown') {
        firstItem.focus();
      } else {
        lastItem.focus();
      }
    }
  };

  const hasSearchItemsWithExtraData = !!searchItems[0]?.extraData;

  const searchItemsContainerClasses = classnames(styles.searchItemsContainer, {
    [styles.searchItemsContainerWithExtraData]: hasSearchItemsWithExtraData,
    [styles.searchItemsContainerWithResults]: !hasSearchItemsWithExtraData,
    [styles.searchItemsContainerWithResultsAndNavigateBack]:
      !hasSearchItemsWithExtraData && canNavigateBack,
    [styles.searchItemsContainerHasError]: !!validationError,
  });

  return (
    <>
      <div
        ref={searchContainer}
        className={classnames(styles.inputContainer, {
          [styles.hasFocus]: hasFocus,
          [styles.hasValue]: value.length > 0,
        })}
        data-test={dataTag}
      >
        <div className={styles.fieldContainer}>
          <SearchInput
            dataTag="address-search-input"
            errorText={validationError}
            id={id}
            onChange={handleInputChange}
            onFocus={handleSearchFocus}
            onBlur={handleSearchBlur}
            onKeyDown={handleKeyDown}
            required={required}
            value={value}
            ariaDescribedBy="address-search_assistiveHint"
          />
          <span className="sr-only" id="address-search_assistiveHint">
            . When results are available use up and down arrows to review and enter to select. Touch
            device users, explore by touch or with swipe gestures.
          </span>
          <span className="sr-only" aria-live="polite">
            {!loading && value.length > 4 && searchItems.length > 0 && (
              <>Address suggestions are available</>
            )}
          </span>
        </div>
        {!error && loading && value.length > 1 && (
          <div className={styles.searchItemsContainer}>
            <ul className={styles.searchItems} role="listbox">
              <li
                key="more-characters-required"
                className={styles.searchItemNoResult}
                role="option"
                data-test="more-character-item"
                aria-setsize={0}
                aria-selected={false}
                tabIndex={-1}
              >
                <Typography element="span">
                  {value.length > 5 ? 'Keep typing\u2026' : 'Keep typing to show addresses'}
                </Typography>
                <Spinner
                  isActive
                  size="small"
                  ariaTextActive="Please wait."
                  className={styles.spinner}
                />
              </li>
            </ul>
          </div>
        )}
        {!loading && value.length > 4 && (
          <div
            className={!addressSelected && hasFocus ? searchItemsContainerClasses : styles.hidden}
          >
            <ul
              className={styles.searchItems}
              role="listbox"
              data-cs-mask
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
            >
              {canNavigateBack && (
                <li
                  key="navigateBack"
                  role="option"
                  aria-setsize={totalItems}
                  aria-posinset={0}
                  aria-selected={false}
                  className={searchResultStyles.searchOption}
                >
                  <SearchNavigateBack
                    onClick={() => {
                      const addressSearch = document.getElementById('address-search');
                      if (addressSearch) {
                        addressSearch.focus();
                      }
                      onNavigateBack();
                    }}
                    onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                      const searchInput = document.getElementById(
                        'address-search',
                      ) as HTMLInputElement;
                      const firstItem = document.getElementById(
                        `${searchItemPrefix}0`,
                      ) as HTMLElement;

                      const { key } = event;

                      if (
                        [
                          keyValues.KEY_ENTER,
                          keyValues.KEY_UP,
                          keyValues.KEY_DOWN,
                          keyValues.KEY_LEFT,
                        ].includes(key)
                      ) {
                        event.preventDefault();
                        event.stopPropagation();
                      }

                      switch (key) {
                        case keyValues.KEY_LEFT:
                        case keyValues.KEY_ENTER:
                          setState({ ...state, addressSelected: false });
                          searchInput.focus();
                          onNavigateBack();
                          break;
                        case keyValues.KEY_UP:
                          searchInput.focus();
                          break;
                        case keyValues.KEY_DOWN:
                          firstItem.focus();
                          break;
                        default:
                          break;
                      }
                    }}
                  />
                </li>
              )}
              {searchItems.map((item, index) => (
                <SearchResult
                  id={`${searchItemPrefix}${item.id}`}
                  key={item.id}
                  item={item}
                  setSize={totalItems}
                  itemNumber={index}
                  onClick={() => {
                    const addressSearch = document.getElementById('address-search');
                    if (addressSearch) {
                      addressSearch.focus();
                    }
                    onSelect(item);

                    if (!item.extraData) {
                      setState({ ...state, addressSelected: true });
                    }
                  }}
                  onKeyDown={(event: KeyboardEvent<HTMLLIElement>) => {
                    const searchInput = document.getElementById(
                      'address-search',
                    ) as HTMLInputElement;
                    const nextItem = document.getElementById(
                      `${searchItemPrefix}${index + 1}`,
                    ) as HTMLElement;

                    let prevItem = canNavigateBack
                      ? document.getElementById('search-navigate-back')
                      : searchInput;

                    if (index > 0) {
                      prevItem = document.getElementById(`${searchItemPrefix}${[index - 1]}`);
                    }

                    const { key } = event;

                    if (
                      [
                        keyValues.KEY_ENTER,
                        keyValues.KEY_UP,
                        keyValues.KEY_DOWN,
                        keyValues.KEY_RIGHT,
                      ].includes(key)
                    ) {
                      event.preventDefault();
                      event.stopPropagation();
                    }

                    switch (key) {
                      case keyValues.KEY_ENTER:
                        onSelect(item);
                        searchInput.focus();
                        if (!item.extraData) {
                          setState({ ...state, addressSelected: true });
                        }
                        break;
                      case keyValues.KEY_RIGHT:
                        if (item.extraData) {
                          onSelect(item);
                          searchInput.focus();
                        }
                        break;
                      case keyValues.KEY_UP:
                        if (prevItem) {
                          prevItem.focus();
                        } else {
                          searchInput.focus();
                        }
                        break;
                      case keyValues.KEY_DOWN:
                        if (nextItem) {
                          nextItem.focus();
                        } else {
                          searchInput.focus();
                        }
                        break;
                      default:
                        break;
                    }
                  }}
                />
              ))}
              {searchItems.length === 0 && !addressSelected && (
                <li key="noResults" tabIndex={-1}>
                  <SearchError apiError={error} />
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    </>
  );
};

export default AddressSearch;
